import { useState, useMemo } from 'react';
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, Bar } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { dateLocale } from '../utils/periodUtils';

const YoutubeResultsChart = ({
  formatNumber,
  selectedPeriod = 'LAST_7_DAYS',
  processedYoutubeData, // Nouvelles données traitées par YoutubeVideosTable
  isLoading = false, // Indicateur de chargement
  granularity = 'day', // Nouvelle prop pour la granularité
  onGranularityChange // Callback pour changer la granularité
}) => {
  const [showChart, setShowChart] = useState(true);

  // Fonction pour formater une date en local (sans conversion UTC)
  // Évite le décalage de -1 jour qui peut survenir avec toISOString()
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fonction pour normaliser les dates au format ISO (YYYY-MM-DD)
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;

    // Déjà au format ISO
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // Format DD/MM/YY
    if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(dateStr)) {
      const parts = dateStr.split('/');
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      let year = parseInt(parts[2], 10);
      year = year >= 50 ? 1900 + year : 2000 + year;
      return `${year}-${month}-${day}`;
    }

    // Essayer de parser comme Date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return formatLocalDate(date);
    }

    return null;
  };

  // Fonction pour combler les dates manquantes avec des zéros
  const fillMissingDates = (data, startDate, endDate) => {
    const normalizedStartDate = normalizeDate(startDate);
    const normalizedEndDate = normalizeDate(endDate);

    if (!normalizedStartDate || !normalizedEndDate) {
      console.warn('📅 [YT-FILL-DATES] Dates invalides, retour données originales');
      return data || [];
    }

    // Minuit LOCAL des deux côtés : voir `dateLocale`. Avec minuit UTC, le curseur gagne une
    // heure au passage à l'heure d'hiver et la boucle rend un jour de moins.
    const start = dateLocale(normalizedStartDate);
    const end = dateLocale(normalizedEndDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
      console.warn(`📅 [YT-FILL-DATES] Problème de dates: ${normalizedStartDate} -> ${normalizedEndDate}`);
      return data || [];
    }

    // Créer une Map des données existantes
    const existingDates = new Map();
    if (data && data.length > 0) {
      for (const item of data) {
        const normalizedDate = normalizeDate(item.date);
        if (normalizedDate) {
          existingDates.set(normalizedDate, item);
        }
      }
    }

    // Générer toutes les dates de la période
    const filledData = [];
    const currentDate = new Date(start);

    while (currentDate <= end) {
      const dateStr = formatLocalDate(currentDate);

      if (existingDates.has(dateStr)) {
        filledData.push({
          ...existingDates.get(dateStr),
          date: dateStr
        });
      } else {
        // Ajouter une ligne avec des zéros pour les dates manquantes
        filledData.push({
          date: dateStr,
          impressions: 0,
          clicks: 0,
          cost: 0,
          views: 0,
          conversions: 0,
          conversionsValue: 0,
          interactions: 0
        });
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    console.log(`📅 [YT-FILL-DATES] ${normalizedStartDate} -> ${normalizedEndDate}: ${data ? data.length : 0} -> ${filledData.length} jours`);

    return filledData;
  };

  // Calculer les dates de début et fin selon la période
  const getPeriodDates = (period) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let startDate, endDate;

    switch (period) {
      case 'TODAY':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'YESTERDAY':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        endDate = new Date(startDate);
        break;
      case 'LAST_7_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 6);
        break;
      case 'LAST_14_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 13);
        break;
      case 'LAST_30_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case 'LAST_90_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 89);
        break;
      case 'LAST_180_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 179);
        break;
      case 'LAST_365_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 364);
        break;
      default:
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 29);
    }

    return {
      startDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate)
    };
  };

  // Définir les options de granularité (identiques au dashboard global)
  const granularityOptions = [
    { value: 'day', label: 'Jour' },
    { value: 'week', label: 'Semaine' },
    { value: 'month', label: 'Mois' },
    { value: 'quarter', label: 'Trimestre' },
    { value: 'year', label: 'Année' }
  ];

  // Fonction pour déterminer le nombre de jours selon la période
  const getDaysFromPeriod = (period) => {
    switch (period) {
      case 'TODAY':
        return 1;
      case 'YESTERDAY':
        return 1;
      case 'LAST_7_DAYS':
        return 7;
      case 'LAST_14_DAYS':
        return 14;
      case 'LAST_30_DAYS':
        return 30;
      case 'LAST_90_DAYS':
        return 90;
      case 'LAST_180_DAYS':
        return 180;
      case 'LAST_365_DAYS':
        return 365;
      case 'THIS_MONTH':
        // Approximation : nombre de jours depuis le début du mois
        const today = new Date();
        return today.getDate();
      case 'LAST_MONTH':
        // Approximation : 30 jours
        return 30;
      case 'THIS_YEAR':
        // Approximation : nombre de jours depuis le début de l'année
        const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
        return dayOfYear;
      case 'LAST_YEAR':
        return 365;
      default:
        // Pour les périodes personnalisées, utiliser 30 jours par défaut
        return 30;
    }
  };

  // Fonction pour enrichir les données avec les périodes de granularité
  const enrichDataWithPeriods = (dailyData) => {
    return dailyData.map(dayData => {
      const date = new Date(dayData.date);
      if (isNaN(date.getTime())) return dayData;
      
      // Calculer les différentes périodes
      const year = date.getFullYear();
      const month = date.getMonth();
      const quarter = Math.floor(month / 3) + 1;
      
      // Calculer le début de la semaine (lundi)
      const dayOfWeek = date.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - daysToMonday);
      
      const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
      const quarterKey = `${year}-Q${quarter}`;
      const yearKey = year.toString();
      
      const weekStartStr = formatLocalDate(weekStart);
      return {
        ...dayData,
        periods: {
          day: dayData.date,
          week: weekStartStr,
          month: monthKey,
          quarter: quarterKey,
          year: yearKey
        },
        labels: {
          day: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
          week: `Sem. ${weekStartStr}`,
          month: `${String(month + 1).padStart(2, '0')}/${year}`,
          quarter: quarterKey,
          year: yearKey
        }
      };
    });
  };
  
  // Fonction pour agréger les données selon la granularité
  const aggregateDataByGranularity = (enrichedData, targetGranularity) => {
    const groupedData = {};
    
    enrichedData.forEach(item => {
      const periodKey = item.periods?.[targetGranularity];
      const label = item.labels?.[targetGranularity];
      
      if (!groupedData[periodKey]) {
        groupedData[periodKey] = {
          date: label || periodKey,
          dateFormatted: label || periodKey,
          periodKey: periodKey, // Ajouter la clé de période pour le tri
          impressions: 0,
          clicks: 0,
          cost: 0,
          views: 0,
          conversions: 0,
          conversionsValue: 0
        };
      }
      
      // Agréger les métriques
      groupedData[periodKey].impressions += item.impressions || 0;
      groupedData[periodKey].clicks += item.clicks || 0;
      groupedData[periodKey].cost += item.cost || 0;
      groupedData[periodKey].views += item.views || 0;
      groupedData[periodKey].conversions += item.conversions || 0;
      groupedData[periodKey].conversionsValue += item.conversionsValue || 0;
    });
    
    // Convertir en tableau et trier
    return Object.values(groupedData).sort((a, b) => {
      // Pour les jours et semaines, trier par la clé de période (format YYYY-MM-DD)
      if (targetGranularity === 'day' || targetGranularity === 'week') {
        return a.periodKey.localeCompare(b.periodKey);
      }
      
      // Pour les autres granularités, trier par label formaté
      return a.dateFormatted.localeCompare(b.dateFormatted);
    });
  };

  // Calcul des données temporelles pour le graphique
  const chartData = useMemo(() => {
    // Utiliser les données traitées par YoutubeVideosTable si disponibles
    if (processedYoutubeData && processedYoutubeData.totals) {
      const { totals, dailyData } = processedYoutubeData;
      console.log('🎥 [YouTube Results] Utilisation des totaux traités par YoutubeVideosTable:', totals);
      console.log(`🎥 [YouTube Results] Données temporelles reçues: ${dailyData ? dailyData.length : 0} jours`);
      
      // Si aucune donnée disponible dans les totaux, retourner un tableau vide
      if (totals.impressions === 0 && totals.cost === 0) {
        console.log('🎥 [YouTube Results] Aucune donnée de performance dans les totaux traités');
        return [];
      }
      
      // NOUVEAU: Utiliser les vraies données jour par jour du backend si disponibles
      if (dailyData && dailyData.length > 0) {
        console.log('🎥 [YouTube Results] Utilisation des vraies données temporelles du backend');
        console.log(`🎥 [YouTube Results] Granularité sélectionnée: ${granularity}`);

        // Combler les dates manquantes avec des zéros (seulement pour granularité jour)
        let completeData = dailyData;
        if (granularity === 'day') {
          const periodDates = getPeriodDates(selectedPeriod);
          console.log(`📅 [YouTube Results] Période calculée: ${periodDates.startDate} -> ${periodDates.endDate}`);
          completeData = fillMissingDates(dailyData, periodDates.startDate, periodDates.endDate);
        }

        // Enrichir les données avec les périodes de granularité
        const enrichedData = enrichDataWithPeriods(completeData);
        
        // Agréger selon la granularité sélectionnée
        const aggregatedData = aggregateDataByGranularity(enrichedData, granularity);
        
        console.log(`🎥 [YouTube Results] Données agrégées (${granularity}):`, aggregatedData.length, 'périodes');
        
        // Vérification des totaux pour cohérence
        const calculatedTotals = aggregatedData.reduce((acc, period) => ({
          impressions: acc.impressions + period.impressions,
          cost: acc.cost + period.cost,
          clicks: acc.clicks + period.clicks,
          views: acc.views + period.views
        }), { impressions: 0, cost: 0, clicks: 0, views: 0 });
        
        console.log('🎥 [YouTube Results] Totaux calculés depuis dailyData agrégées:', calculatedTotals);
        console.log('🎥 [YouTube Results] Totaux de référence:', { impressions: totals.impressions, cost: totals.cost, clicks: totals.clicks, views: totals.views });
        
        return aggregatedData;
      }
      
      // FALLBACK: Si pas de données temporelles, générer une distribution uniforme (ancien comportement)
      console.log('🎥 [YouTube Results] Pas de données temporelles, fallback vers distribution uniforme');
      
      // Utiliser les totaux directement
      const totalStats = {
        impressions: totals.impressions || 0,
        cost: totals.cost || 0,
        clicks: totals.clicks || 0,
        views: totals.views || 0,
        conversions: totals.conversions || 0,
        conversionsValue: totals.conversionsValue || 0
      };
      
      // Déterminer le nombre de jours selon la période sélectionnée
      const numberOfDays = getDaysFromPeriod(selectedPeriod);
      console.log(`🎥 [YouTube Results] Période ${selectedPeriod}: ${numberOfDays} jours`);
      
      // Créer des données de démonstration sur la période sélectionnée
      const today = new Date();
      
      // Déterminer la date de fin selon la période
      let endDate = new Date(today);
      if (selectedPeriod === 'YESTERDAY') {
        endDate.setDate(today.getDate() - 1);
        console.log(`🎥 [YouTube Results] YESTERDAY - Date calculée: ${formatLocalDate(endDate)} (aujourd'hui: ${formatLocalDate(today)})`);
      } else if (selectedPeriod === 'TODAY') {
        endDate = new Date(today);
        console.log(`🎥 [YouTube Results] TODAY - Date calculée: ${formatLocalDate(endDate)}`);
      } else {
        endDate.setDate(today.getDate() - 1);
        console.log(`🎥 [YouTube Results] ${selectedPeriod} - Date de fin calculée: ${formatLocalDate(endDate)}`);
      }

      const timelineData = Array.from({ length: numberOfDays }, (_, i) => {
        const date = new Date(endDate);
        date.setDate(date.getDate() - (numberOfDays - 1 - i));

        const dayData = {
          date: formatLocalDate(date),
          impressions: Math.round(totalStats.impressions / numberOfDays),
          cost: parseFloat((totalStats.cost / numberOfDays).toFixed(2)),
          clicks: Math.round(totalStats.clicks / numberOfDays),
          views: Math.round(totalStats.views / numberOfDays),
          conversions: parseFloat((totalStats.conversions / numberOfDays).toFixed(1)),
          conversionsValue: parseFloat((totalStats.conversionsValue / numberOfDays).toFixed(2))
        };
        
        return dayData;
      });
      
      console.log('🎥 [YouTube Results] timelineData fallback générées:', timelineData.length, 'jours');

      return timelineData.map(day => ({
        ...day,
        dateFormatted: day.date // Les données fallback gardent un formatage simple
      }));
    }

    // Fallback si pas de données traitées
    console.log('🎥 [YouTube Results] Pas de données traitées disponibles, affichage vide');
    return [];
  }, [processedYoutubeData, selectedPeriod, granularity]);

  // Vérifier si nous avons des données à afficher
  const hasData = chartData && chartData.length > 0 && chartData.some(day => day.impressions > 0 || day.cost > 0);

  // Debug: vérifier les conditions de chargement
  console.log('🔍 [YouTube Results] isLoading:', isLoading, 'processedYoutubeData:', !!processedYoutubeData);

  // Affichage de l'écran de chargement (même style que Mots-clés par Score de Qualité)
  if (isLoading || !processedYoutubeData) {
    console.log('🔄 [YouTube Results] Affichage écran de chargement');
    return (
      <div className="performance-chart-section">
        <div className="section-header">
          <h4><TrendingUp size={16} />&nbsp;Résultats</h4>
        </div>
        <div className="loading-state" style={{ 
          flexDirection: 'column',
          padding: '60px 40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <div className="loading-spinner"></div>
          Chargement des résultats YouTube...
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="performance-chart-section">
        <div className="section-header">
          <h4><TrendingUp size={16} />&nbsp;Résultats</h4>
        </div>
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: 'white', 
          borderRadius: '12px',
          color: '#6b7280',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '2em', marginBottom: '16px', opacity: 0.5 }}>📊</div>
          <p>Aucune donnée de performance trouvée dans les vidéos YouTube</p>
          <p style={{ fontSize: '0.9em', marginTop: '8px' }}>
            Vérifiez que vos vidéos ont des impressions ou des coûts associés
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="performance-chart-section">
      <div className="section-header">
        <h4><TrendingUp size={16} />&nbsp;Résultats</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Filtres de granularité (identiques au dashboard global) */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {granularityOptions.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => onGranularityChange?.(value)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: granularity === value ? '#1a1a1a' : 'white',
                  color: granularity === value ? 'white' : '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                title={`Afficher par ${label.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
          </div>
          
          <button 
            onClick={() => setShowChart(!showChart)}
            className="section-toggle-btn"
            title={showChart ? "Masquer la section" : "Afficher la section"}
          >
            {showChart ? '−' : '+'}
          </button>
        </div>
      </div>

      {showChart && (
        <div className="full-width-chart" style={{ 
          width: '100%', 
          height: '400px', 
          background: 'white', 
          borderRadius: '12px', 
          padding: '20px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart 
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="dateFormatted" 
                fontSize={11} 
                tick={{ fill: '#666' }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={chartData.length > 30 ? Math.floor(chartData.length / 10) : 0}
              />
              <YAxis 
                yAxisId="impressions"
                orientation="left"
                fontSize={11}
                tick={{ fill: '#666' }}
                tickFormatter={(value) => formatNumber(value)}
              />
              <YAxis 
                yAxisId="cost"
                orientation="right"
                fontSize={11}
                tick={{ fill: '#666' }}
                tickFormatter={(value) => `€${formatNumber(value, 2)}`}
              />
              
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div style={{
                        backgroundColor: 'white',
                        padding: '12px',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{label}</p>
                        {payload.map((entry, index) => (
                          <p key={index} style={{ 
                            margin: '4px 0', 
                            color: entry.color,
                            fontSize: '14px'
                          }}>
                            <span style={{ fontWeight: '500' }}>{entry.name}:</span>{' '}
                            {entry.dataKey === 'cost' ? 
                              `€${formatNumber(entry.value, 2)}` : 
                              formatNumber(entry.value)
                            }
                          </p>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="rect"
              />
              
              {/* Histogramme pour les impressions */}
              <Bar 
                yAxisId="impressions"
                dataKey="impressions" 
                name="Impressions"
                fill="#8884d8"
                fillOpacity={0.8}
                radius={[2, 2, 0, 0]}
              />
              
              {/* Courbe pour les coûts */}
              <Line 
                yAxisId="cost"
                type="monotone" 
                dataKey="cost" 
                name="Coût"
                stroke="#82ca9d" 
                strokeWidth={3}
                dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#82ca9d' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default YoutubeResultsChart;